import type { Airport } from '@/types';

/**
 * Convert latitude/longitude to 3D position on sphere
 */
export function latLonToVector3(
  lat: number,
  lon: number,
  radius: number = 1.01
): [number, number, number] {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);

  const x = -radius * Math.sin(phi) * Math.cos(theta);
  const y = radius * Math.cos(phi);
  const z = radius * Math.sin(phi) * Math.sin(theta);

  return [x, y, z];
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in miles
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate the heading (bearing) between two points
 * Returns angle in radians
 */
export function calculateHeading(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;

  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  return Math.atan2(y, x);
}

/**
 * Generate arc points between two airports
 */
export function generateArcPoints(
  from: Airport,
  to: Airport,
  segments: number = 100,
  arcHeightFactor: number = 0.15
): [number, number, number][] {
  const fromVec = latLonToVector3(from.lat, from.lon);
  const toVec = latLonToVector3(to.lat, to.lon);
  const points: [number, number, number][] = [];

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;

    // Interpolate between the two points
    const x = fromVec[0] + (toVec[0] - fromVec[0]) * t;
    const y = fromVec[1] + (toVec[1] - fromVec[1]) * t;
    const z = fromVec[2] + (toVec[2] - fromVec[2]) * t;

    // Calculate distance for arc height
    const distance = Math.sqrt(
      (toVec[0] - fromVec[0]) ** 2 +
      (toVec[1] - fromVec[1]) ** 2 +
      (toVec[2] - fromVec[2]) ** 2
    );
    const arcHeight = 0.2 + distance * arcHeightFactor;

    // Push the point outward to create the arc
    const mid = Math.sin(t * Math.PI);
    const length = Math.sqrt(x * x + y * y + z * z);
    const normalizedRadius = 1.01 + arcHeight * mid;
    const scale = normalizedRadius / length;

    points.push([x * scale, y * scale, z * scale]);
  }

  return points;
}

/**
 * Get midpoint between two airports (for camera focus)
 */
export function getMidpoint(from: Airport, to: Airport): { lat: number; lon: number } {
  return {
    lat: (from.lat + to.lat) / 2,
    lon: (from.lon + to.lon) / 2,
  };
}
